<template>
  <v-container>
    <v-card flat>
      <v-card-text>
        <v-row class="mb-4">
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="search"
              :label="$t($ts.giftCardManagement.batches.search)"
              append-icon="mdi-magnify"
              dense
              outlined
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-select
              v-model="statusFilter"
              :items="statusFilterOptions"
              :label="$t($ts.giftCardManagement.batches.exportStatus)"
              dense
              outlined
            />
          </v-col>
        </v-row>

        <v-data-table
          :headers="tableHeaders"
          :items="filteredBatches"
          :loading="loading"
          :search="search"
          class="elevation-1"
        >
          <template #item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <template #item.printedAt="{ item }">
            <span v-if="item.printedAt">{{ formatDate(item.printedAt) }}</span>
            <span v-else>-</span>
          </template>

          <template #item.exportedAt="{ item }">
            <span v-if="item.exportedAt">{{ formatDate(item.exportedAt) }}</span>
            <span v-else>-</span>
          </template>

          <template #item.actions="{ item }">
            <v-tooltip bottom>
              <template #activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                  @click="exportBatch(item)"
                >
                  <v-icon>mdi-file-download</v-icon>
                </v-btn>
              </template>
              <span>{{ $t($ts.giftCardManagement.batches.downloadPdf) }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <gift-card-batch-dialog
      v-model="batchDialogOpen"
      :types="giftCardTypes"
      :form-data="batchForm"
      @generate="handleBatchGenerated"
    />
  </v-container>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { giftCardsModule } from "@/store/modules/giftCards/giftCardsModule";
import { IGiftCardBatchDTO, IGiftCardTypeDTO, IGenerateGiftCardBatchDTO } from "@shared/types";
import GiftCardBatchDialog from "@/components/giftcards/GiftCardBatchDialog.vue";

type StatusFilter = "all" | "exported" | "pending";

@Component({
  name: "gift-card-batches-settings",
  components: {
    GiftCardBatchDialog,
  },
})
export default class GiftCardBatchesSettings extends Vue {
  search = "";
  statusFilter: StatusFilter = "all";
  batchDialogOpen = false;
  batchForm: IGenerateGiftCardBatchDTO & { name: string; notes: string } = {
    giftCardTypeId: null as any,
    quantity: 10,
    name: "",
    notes: "",
  };

  get loading(): boolean {
    return giftCardsModule.loading;
  }

  get batches(): IGiftCardBatchDTO[] {
    return giftCardsModule.batches;
  }

  get filteredBatches(): IGiftCardBatchDTO[] {
    if (this.statusFilter === "all") {
      return this.batches;
    }
    if (this.statusFilter === "exported") {
      return this.batches.filter((batch) => !!batch.exportedAt);
    }
    return this.batches.filter((batch) => !batch.exportedAt);
  }

  get giftCardTypes(): IGiftCardTypeDTO[] {
    return giftCardsModule.types;
  }

  get tableHeaders() {
    return [
      { text: this.$t(this.$ts.giftCardManagement.batches.name).toString(), value: "name" },
      { text: this.$t(this.$ts.giftCardManagement.batches.type).toString(), value: "giftCardType.name" },
      { text: this.$t(this.$ts.giftCardManagement.batches.quantity).toString(), value: "quantity" },
      { text: this.$t(this.$ts.giftCardManagement.batches.created).toString(), value: "createdAt" },
      { text: this.$t(this.$ts.giftCardManagement.batches.printed).toString(), value: "printedAt" },
      { text: this.$t(this.$ts.giftCardManagement.batches.exported).toString(), value: "exportedAt" },
      { text: this.$t(this.$ts.giftCardManagement.batches.actions).toString(), value: "actions", sortable: false },
    ];
  }

  get statusFilterOptions() {
    return [
      { text: this.$t(this.$ts.giftCardManagement.batches.allBatches).toString(), value: "all" },
      { text: this.$t(this.$ts.giftCardManagement.batches.exportedBatches).toString(), value: "exported" },
      { text: this.$t(this.$ts.giftCardManagement.batches.notExported).toString(), value: "pending" },
    ];
  }

  formatDate(value: string | Date | null | undefined): string {
    if (!value) {
      return "-";
    }
    const date = typeof value === "string" ? new Date(value) : value;
    return date.toLocaleDateString(this.$i18n.locale);
  }

  async exportBatch(batch: IGiftCardBatchDTO): Promise<void> {
    const success = await giftCardsModule.exportBatch({ batchId: batch.id });
    if (success) {
      await giftCardsModule.fetchBatches();
    }
  }

  generateBatch(): void {
    this.batchDialogOpen = true;
  }

  async handleBatchGenerated(data: IGenerateGiftCardBatchDTO): Promise<void> {
    try {
      await giftCardsModule.generateBatch(data);
      this.batchDialogOpen = false;
      this.batchForm = {
        giftCardTypeId: null as any,
        quantity: 10,
        name: "",
        notes: "",
      };
      await giftCardsModule.fetchBatches();
    } catch (error) {
      console.error("Failed to generate batch:", error);
    }
  }

  async mounted(): Promise<void> {
    await Promise.all([
      giftCardsModule.fetchBatches(),
      giftCardsModule.fetchTypes(),
    ]);
  }
}
</script>
